import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, MapPin, Calendar, Play } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import ShareEventButton from "./ShareEventButton";
import { fmtEventDates, pick, ddmm } from "./eventUtils";
import EventCountdown from "./EventCountdown";

const KIND = {
    race: { it: "Gara", en: "Race" }, course: { it: "Corso", en: "Camp" }, ride: { it: "Ride", en: "Ride" }, other: { it: "Evento", en: "Event" },
};

/* Event card — poster shown whole (object-contain) so no text/logo is ever cropped. */
export default function EventCard({ event: ev, index }) {
    const { lang } = useLang();
    const en = lang === "en";
    const past = ev.status === "past";
    const to = `/eventi/${ev.slug}`;
    const kind = KIND[ev.kind] || KIND.other;
    return (
        <article data-testid={`event-card-${ev.slug}`} data-status={ev.status}
            className={`group relative flex flex-col bg-abnormal-charcoal border ${past ? "border-white/10" : "border-white/15 hover:border-abnormal-acid"} transition-colors`}>
            <Link to={to} className="relative block bg-black aspect-[4/5] overflow-hidden" aria-label={pick(ev, "title", lang)}>
                {ev.poster ? (
                    <img src={ev.poster} alt={pick(ev, "title", lang)} loading="lazy" decoding="async"
                        className={`w-full h-full object-contain ${past ? "grayscale-[35%] group-hover:grayscale-0" : ""} transition-[filter] duration-300`} />
                ) : (
                    <div className="w-full h-full halftone flex items-center justify-center font-display uppercase text-4xl text-abnormal-paperDim">{ddmm(ev.start_date)}</div>
                )}
                <span className={`absolute top-3 left-3 label-mono px-2 py-1 border ${past ? "bg-black/85 text-abnormal-paperDim border-white/20" : "bg-abnormal-acid text-black border-abnormal-acid"}`}>
                    {past ? (en ? "PAST EVENT" : "EVENTO PASSATO") : (en ? kind.en.toUpperCase() : kind.it.toUpperCase())}
                </span>
                {!past && (
                    <span data-testid={`event-card-days-${ev.slug}`} className="absolute top-3 right-3 label-mono bg-black/85 border border-abnormal-acid/40 px-2 py-1">
                        <EventCountdown ev={ev} lang={lang} compact testid={`event-card-countdown-${ev.slug}`} />
                    </span>
                )}
                {past && ev.video_url && (
                    <span className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 label-mono bg-black/85 text-abnormal-paper border border-white/20 px-2 py-1"><Play size={10} /> VIDEO</span>
                )}
                {typeof index === "number" && <span className="absolute bottom-3 left-3 font-vt text-[10px] tracking-[0.3em] text-abnormal-paperDim/70">N° {String(index + 1).padStart(2, "0")}</span>}
            </Link>
            <div className="flex flex-col flex-1 p-4 md:p-5">
                {ev.series && <p className="label-mono text-abnormal-magenta truncate">{ev.series}</p>}
                <h3 className="mt-1 font-display uppercase leading-[0.9] tracking-tight text-abnormal-paper text-2xl md:text-3xl">
                    <Link to={to} className="hover:text-abnormal-acid transition-colors">{pick(ev, "title", lang)}</Link>
                </h3>
                <p className="mt-3 flex flex-wrap gap-x-4 gap-y-1 label-mono text-abnormal-paperDim">
                    <span className="inline-flex items-center gap-1.5"><Calendar size={11} className="text-abnormal-acid" /> {fmtEventDates(ev, lang)}</span>
                    <span className="inline-flex items-center gap-1.5"><MapPin size={11} className="text-abnormal-acid" /> {ev.location}</span>
                </p>
                <p className="mt-3 text-sm leading-relaxed text-abnormal-paperDim line-clamp-3">{pick(ev, "summary", lang)}</p>
                {ev.abnormal_role && <p className="mt-3 label-mono text-abnormal-acid">ABNORMAL · {pick(ev, "abnormal_role", lang)}</p>}
                <div className="mt-auto pt-5 flex flex-wrap items-center gap-2">
                    <Link to={to} data-testid={`event-card-view-${ev.slug}`} className="btn-primary !px-5 !py-2.5 !text-xs">
                        {en ? "View event" : "View event"} <ArrowRight size={13} />
                    </Link>
                    {!past && ev.registration_url && (
                        <a href={ev.registration_url} target="_blank" rel="noreferrer" data-testid={`event-card-register-${ev.slug}`}
                            className="inline-flex items-center gap-1.5 border border-abnormal-magenta text-abnormal-magenta hover:bg-abnormal-magenta hover:text-black px-4 py-2.5 text-xs uppercase tracking-[0.2em] font-bold transition-colors">
                            {en ? "Book now" : "Iscriviti"} <ArrowUpRight size={12} />
                        </a>
                    )}
                    <ShareEventButton event={ev} size="sm" className="ml-auto" />
                </div>
            </div>
        </article>
    );
}
